import { DefaultListPageSize } from './DashboardUI';
import { useDashboardData } from './useDashboardData';
import { useEffect, useState } from 'react';
import type { IAsyncDataResult } from './useDashboardData';

/** File-local constants for the paginated-list hook. */
const localConstants = {
  // Debounce (ms) applied to the search box so a fresh server query is not issued on every keystroke.
  searchDebounceMs: 400
} as const;

/** The state and data a list page needs to drive a {@link ListControls} panel over a server-paginated, searchable collection. */
export interface IPaginatedList<T> {
  search: string; // The live search box value.
  setSearch: (value: string) => void; // Updates the search box (debounced before it hits the server).
  offset: number; // The current 0-based row offset.
  setOffset: (offset: number) => void; // Pages the list.
  limit: number; // The current page size.
  setLimit: (limit: number) => void; // Changes the page size (resets to the first page).
  data: T[]; // The current page of rows.
  total: number; // The total number of matching rows.
  loading: boolean; // Whether the current page is loading.
  error: unknown | null; // The last fetch error, if any.
  reload: () => void; // Forces a refetch of the current page and total.
}

/**
 * Drives a server-paginated, searchable list for a dashboard page: owns the search text (debounced), the offset, and the page size, and fetches both the current page and the matching total so
 * the shared {@link ListControls} panel can render an accurate "from-to / total" readout. Changing the search term or the page size resets to the first page. Both the page fetch and the count
 * fetch carry the same (debounced) search term, so the visible rows and the total always agree.
 *
 * @param list - Fetches one page: `(signal, offset, limit, pattern?)`. `pattern` is undefined when the search box is empty.
 * @param count - Fetches the matching total: `(signal, pattern?)`.
 * @param deps - Extra dependencies (e.g. the tenant id) that, when changed, refetch both the page and the total.
 * @returns The list state and data for a {@link ListControls}-driven page.
 */
export function usePaginatedList<T>(
  list: (signal: AbortSignal, offset: number, limit: number, pattern?: string) => Promise<T[]>,
  count: (signal: AbortSignal, pattern?: string) => Promise<number>,
  deps: ReadonlyArray<unknown>
): IPaginatedList<T> {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState<number>(DefaultListPageSize);

  // Debounce the search box so partially typed terms do not each trigger a server query.
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(search), localConstants.searchDebounceMs);
    return () => window.clearTimeout(handle);
  }, [search]);

  // A new search term or page size always restarts at the first page. The extra `deps` (e.g. the tenant id) are spread in intentionally so a tenant switch also resets paging.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setOffset(0), [debounced, limit, ...deps]);

  const pattern = debounced.trim() || undefined;
  const page: IAsyncDataResult<T[]> = useDashboardData((signal) => list(signal, offset, limit, pattern), [offset, limit, debounced, ...deps]);
  const totalResult: IAsyncDataResult<number> = useDashboardData((signal) => count(signal, pattern), [debounced, ...deps]);

  return {
    search,
    setSearch,
    offset,
    setOffset,
    limit,
    setLimit,
    data: page.data ?? [],
    total: totalResult.data ?? 0,
    loading: page.loading,
    error: page.error,
    reload: page.reload
  };
}
